<template>
  <div class="facilities-table-filter">
    <button class="facilities-table-filter__button" @click="openFiltersModal">
      <span>{{ $t("Filter") }}</span>
    </button>
    <BaseInput
      v-model="search"
      no-error
      clearable
      icon="search"
      class="facilities-table-filter__search-input"
      :inner-placeholder="$t('Search')"
    />
    <BaseSelect
      :options="availableLevelsGetter"
      :model-value="selectedLevelsBySubscriptionModel"
      styled
      arrow
      multiple
      checkbox
      full-height
      class="facilities-table-filter__select"
      @change="setSelectedLevel($event)"
    >
      <template #value>
        <template v-if="selectedLevelsBySubscriptionModel?.length">
          <span
            v-for="(level, index) in selectedLevelsBySubscriptionModel"
            :key="`level-${level.id}`"
          >
            <template v-if="index < 2"
            >{{ index === 0 ? "" : "," }} {{ level.value }}</template
            >
            <template v-if="index === 2">...</template>
          </span>
        </template>
      </template>
      <template #first-option>
        <div
          class="select-option"
          @click="setSelectedLevels(availableLevelsGetter)"
        >
          {{ $t("Select all") }}
        </div>
      </template>
      <template #option="option">
        {{ option.option.value }}
      </template>
    </BaseSelect>
    <BaseSelect
      styled
      arrow
      radio
      :options="citiesList"
      :model-value="selectedCity"
      class="facilities-table-filter__select"
      @change="setSelectedCities($event)"
    >
      <template #value>{{ selectedCity ? $t(selectedCity.value) || selectedCity.value : undefined }}</template>
      <template #option="option">{{ $t(option.option.value) }}</template>
    </BaseSelect>
    <BaseSelect
      :options="filteredActivities"
      :model-value="selectedActivities"
      styled
      arrow
      multiple
      checkbox
      :is-tooltip="true"
      class="facilities-table-filter__select"
      @change="setSelectedActivities($event)"
      searchable
      :clear-search="resetSearchActivities"
    >
      <template #value>
        <template v-if="selectedActivities?.length">
          <span
            v-for="(activity, index) in selectedActivities"
            :key="`level-${activity.id}`"
          >
            <template v-if="index < 2"
            >{{ index === 0 ? "" : "," }} {{ activity.value }}</template
            >
            <template v-if="index === 2">...</template>
          </span>
        </template>
        <span v-else>{{ $t("Activity") }}</span>
      </template>
      <template #first-option>
        <div class="select-option" @click="resetSelectedActivities">
          {{ $t("Reset all") }}
        </div>
      </template>
      <template #search>
        <BaseInput
          v-model="searchActivities"
          no-error
          clearable
          class="select-search-option"
          icon="search"
          :inner-placeholder="$t('Search')"
        />
      </template>
      <template #option="option">
        {{ option.option.value }}
      </template>
    </BaseSelect>
    <BaseSelect
      :options="tags"
      :model-value="selectedTags"
      styled
      arrow
      multiple
      checkbox
      :is-tooltip="true"
      class="facilities-table-filter__select"
      @change="setSelectedTags($event)"
    >
      <template #value>
        <template v-if="selectedTags.length">
          <span
            v-for="(tag, index) in selectedTags"
            :key="`tag-value=${tag.id}`"
          >
            {{ index === 0 ? "" : "," }} {{ tag.value }}
          </span>
        </template>
        <span v-else>{{ $t("More") }}</span>
      </template>
      <template #first-option>
        <div class="select-option" @click="resetSelectedTags">
          {{ $t("Reset all") }}
        </div>
      </template>
      <template #option="tagsOptionalList">
        {{ tagsOptionalList.option.value }}
      </template>
    </BaseSelect>

    <LazyModalsFiltersModal
      v-if="showFiltersModal"
      :is-show-modal="showFiltersModal"
      @close="closeFiltersModal"
      @set-filter-cities="setSelectedCities"
      @set-filter-levels="setSelectedLevel"
      @set-filter-activities="setSelectedActivities"
      @set-filter-tags="setSelectedTags"
      @reset-filters="resetFilters()"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { storeToRefs } from "pinia";
import debounce from "lodash/debounce";
import BaseInput from "~/components/input/BaseInput.vue";
import BaseSelect, { type BaseSelectOption } from "~/components/input/BaseSelect.vue";
import { useConfigStore } from "~/stores/config";
import { useTableFilterStore } from "~/stores/tableFilter";
import { getTableFacilities } from "~/api/table";
import { useCountriesStore } from "~/stores/countries";
import { ILevelFilter } from "~/server/types/server.interface";

const tableFilterStore = useTableFilterStore();
const {
  search,
  searchActivities,
  isLoadingData,
  facilities,
  translatedActivities,
  selectedActivities,
  citiesList,
  selectedCity,
  availableLevelsGetter,
  selectedLevels,
  tags,
  selectedTags,
  subscriptionModel,
  selectedLevelsBySubscriptionModel,
} = storeToRefs(tableFilterStore);

const {
  setSelectedCities,
  resetSelectedCity,

  setSelectedActivities,
  resetSelectedActivities,
  prepareSelectedActivities,

  setSelectedLevel,
  setSelectedLevels,
  getSelectedLevelIds,

  setSelectedTags,
  resetSelectedTags,
} = tableFilterStore;

const { currentCountry } = storeToRefs(useCountriesStore());

const resetSearch = () => {
  search.value = "";
};
const resetSearchActivities = () => {
  searchActivities.value = "";
};
const searchFacilities = debounce(async () => {
  try {
    facilities.value = await getTableFacilities({
      country: currentCountry.value.code,
      search: search.value,
      levels: getSelectedLevelIds(),
      city: selectedCity.value?.id,
      activities: prepareSelectedActivities(),
      subscriptionModel: subscriptionModel.value,
      tags: selectedTags.value.map(tag => tag.id),
    });
  } catch (error) {
    if (error instanceof Error) {
      console.log(`Error: ${error.message}`);
    }
  } finally {
    isLoadingData.value = false;
  }
}, 350);

const showFiltersModal = ref(false);

const openFiltersModal = () => {
  showFiltersModal.value = true;
};

const closeFiltersModal = () => {
  showFiltersModal.value = false;
};

const resetFilters = () => {
  setSelectedLevels(availableLevelsGetter.value);
  resetSelectedActivities();
  resetSelectedCity();
  resetSelectedTags();
};

const filteredActivities = computed(() => {
  const searchValue = searchActivities.value.toLowerCase();

  if (!searchValue) {
    return translatedActivities.value;
  }

  const searchTerms = searchValue.split(" ");

  const isSearchTermMatched = ({ value }: ILevelFilter): boolean => {
    const itemValue = value.toLowerCase();
    return searchTerms.every(term => itemValue.includes(term));
  };

  return translatedActivities.value.filter(isSearchTermMatched);
});

watch(
  [search, selectedCity, selectedLevels, selectedActivities, selectedTags],
  () => {
    searchFacilities();
  },
  { deep: true }
);

onUnmounted(() => {
  resetSearch();
  resetSelectedActivities();
});
</script>
